// taskTypeMeta.js
// Shared metadata for task_type rendering across Tasks, TaskDetail,
// TaskActionModal, RemindersList, and calendar task chips.

export const ENGAGEMENT_ACCENT = '#16A34A';

export const TASK_TYPE_META = {
  inbound_call: {
    label: 'Inbound Call',
    icon: 'fa-sharp fa-regular fa-phone',
    accent: ENGAGEMENT_ACCENT
  },
  consultation: {
    label: 'Consultation',
    icon: 'fa-sharp fa-regular fa-calendar-clock',
    accent: ENGAGEMENT_ACCENT
  },
  materials_check: {
    label: 'Materials Check',
    icon: 'fa-sharp fa-regular fa-box-check',
    accent: '#2563EB'
  },
  reorder: {
    label: 'Reorder',
    icon: 'fa-sharp fa-regular fa-cart-plus',
    accent: '#2563EB'
  },
  invoice_recovery: {
    label: 'Invoice Recovery',
    icon: 'fa-sharp fa-regular fa-file-invoice-dollar',
    accent: '#DC2626'
  }
};

const DEFAULT_META = {
  label: 'Reminder',
  icon: 'fa-sharp fa-regular fa-bell',
  accent: '#8B5CF6'
};

export const getTaskTypeMeta = (taskType) => TASK_TYPE_META[taskType] || DEFAULT_META;

// ---------------------------------------------------------------------------
// Engagement types — a DISPLAY category (green accent, GHL automation).
//
// Deliberately NOT the same set as "requires an outcome". `invoice_recovery`
// requires an outcome but is not an engagement task, and conflating the two
// would paint it green and file it with the call/consultation follow-ups.
// Use `requiresOutcome()` for the Complete gate; this one is for styling.
// ---------------------------------------------------------------------------
export const ENGAGEMENT_TYPES = ['inbound_call', 'consultation'];

export const isEngagementType = (taskType) => ENGAGEMENT_TYPES.includes(taskType);

export const ENGAGEMENT_OUTCOMES = [
  {
    value: 'contact_attempted',
    label: 'Attempted — no answer / left message',
    hint: 'Keeps the customer’s re-engagement follow-ups going.'
  },
  {
    value: 'handled_no_action',
    label: 'Handled — no further action needed',
    hint: 'Stops the "we missed you" sequence.'
  }
];

/**
 * Invoice-recovery outcomes.
 *
 * Manual completion is the loophole in "every payment has an invoice". Without
 * a recorded outcome a human can tick one of these closed while no invoice ever
 * gets raised — the exact silent state the task exists to catch, now wearing a
 * checkmark. So the outcome is COLLECTED, never defaulted: a hardcoded
 * `written_off` would defeat the guard more thoroughly than the original bug.
 */
export const INVOICE_RECOVERY_OUTCOMES = [
  {
    value: 'invoice_created_externally',
    label: 'Invoice raised outside Jobvious',
    hint: 'QuickBooks, paper, or another system. The money is accounted for elsewhere.'
  },
  {
    value: 'written_off',
    label: 'Written off — not collectable',
    hint: 'No invoice will be raised. Recorded as a deliberate write-off.'
  },
  {
    value: 'duplicate_payment',
    label: 'Duplicate payment — no invoice owed',
    hint: 'The payment was taken twice; nothing further is due.'
  },
  {
    value: 'refunded',
    label: 'Refunded — no invoice owed',
    hint: 'The payment went back to the customer.'
  },
  {
    value: 'customer_unreachable',
    label: 'Customer unreachable — recorded for the books',
    hint: 'Closed without resolution. Leaves an auditable trail of the gap.'
  }
];

// Task types whose Complete action REQUIRES a resolution_type. The server
// returns 400 without one; on current dev data 7 of 50 tasks are affected,
// which is why this reads as intermittent rather than broken.
export const OUTCOME_OPTIONS_BY_TYPE = {
  inbound_call: ENGAGEMENT_OUTCOMES,
  consultation: ENGAGEMENT_OUTCOMES,
  invoice_recovery: INVOICE_RECOVERY_OUTCOMES
};

/** Does completing this task type require an outcome? */
export const requiresOutcome = (taskType) =>
  Object.prototype.hasOwnProperty.call(OUTCOME_OPTIONS_BY_TYPE, taskType);

/**
 * Outcome choices for a task type, optionally narrowed to what the SERVER says
 * it will accept.
 *
 * A 400 carries an authoritative `allowed` array. Preferring it means a value
 * added server-side still renders (as a humanised fallback label) instead of
 * leaving the user stuck with a picker whose every option is rejected.
 */
export const getOutcomeOptions = (taskType, allowed) => {
  const known = OUTCOME_OPTIONS_BY_TYPE[taskType] || [];
  if (!Array.isArray(allowed) || allowed.length === 0) return known;
  return allowed.map(
    (value) =>
      known.find((o) => o.value === value) || {
        value,
        label: String(value).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        hint: ''
      }
  );
};

/** The question the picker asks — recovery is not "how did it go?". */
export const getOutcomePrompt = (taskType) =>
  taskType === 'invoice_recovery'
    ? 'How was this resolved?'
    : 'How did this turn out?';

/**
 * @deprecated Use ENGAGEMENT_OUTCOMES, or getOutcomeOptions(taskType).
 * Kept so nothing silently renders an empty picker mid-refactor.
 */
export const OUTCOME_OPTIONS = ENGAGEMENT_OUTCOMES;
